import { Injectable } from '@angular/core';
import { isNullish } from '@commonHelpers/math-utils';
import { RequestService } from '@commonServices/request.service';
import { Client as AnalyticsApi, DatePickerRange, InCatalogCatalogSearchesPerMonthDto, InCatalogSearchFilterTagsDto, InCatalogSearchTermsHitDto, InCatalogSearchTermsWithoutHitDto, InCatalogSearchesAvgDto, InCatalogSearchesBenchmarkDto, InCatalogSearchesDto, InCatalogSearchesPerDateDto, InCatalogSearchesPerWeekDto } from '@interfaces/HttpClient/AnalyticsApiModels';
import { firstValueFrom } from 'rxjs';
import { DaterangeService } from './daterange.service';

@Injectable()
export class CatalogDashboardInCatalogSearchService {

	constructor(
		private analyticsApi: AnalyticsApi,
		private requestService: RequestService,
		private daterangeService: DaterangeService) { }

	async getInCatalogSearchesCountOfCatalogAsync(catalogId: number, dateRange?: DatePickerRange): Promise<InCatalogSearchesDto> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetInCatalogSearchesCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetInCatalogSearchesCountOfCatalog(catalogId, range.rangeStart, range.rangeEnd));
			}

		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getInCatalogSearchesCountBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<InCatalogSearchesBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetInCatalogSearchesCountBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getInCatalogSearchesCountCategoryBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<InCatalogSearchesBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetInCatalogSearchesCountCategoryBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getInCatalogSearchesCountCategoryGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<InCatalogSearchesBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSearchBenchmark_GetInCatalogSearchesCountCategoryGroupBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getInCatalogSearchTermsHitCountAsync(catalogId: number): Promise<InCatalogSearchTermsHitDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetSearchTermsHitCount(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getInCatalogSearchFilterTagsCountOfCatalogAsync(catalogId: number): Promise<InCatalogSearchFilterTagsDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetInCatalogSearchFilterTagsCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getInCatalogSearchTermsWithoutHitCountAsync(catalogId: number): Promise<InCatalogSearchTermsWithoutHitDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetInCatalogSearchTermsWithoutHitCount(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getInCatalogSearchesCountOfCatalogAverageAsync(catalogId: number): Promise<InCatalogSearchesAvgDto> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetSearchesCountOfCatalogAverages(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getInCatalogSearchesCountOfCatalogPerDateAsync(catalogId: number): Promise<InCatalogSearchesPerDateDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetSearchesCountOfCatalogPerDate(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getInCatalogSearchesCountOfCatalogPerWeekAsync(catalogId: number): Promise<InCatalogSearchesPerWeekDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetSearchesCountOfCatalogPerWeek(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getInCatalogSearchesCountOfCatalogPerMonthAsync(catalogId: number): Promise<InCatalogCatalogSearchesPerMonthDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminInCatalogSearch_GetSearchesCountOfCatalogPerMonth(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

}
